import { useEffect, useRef, useState, type ReactNode } from "react";
import { Box, Button, ButtonBase, Typography } from "@mui/material";
import { useStepScroll } from "@/shared/motion/step-scroll";

export interface SelectionFlowStep<Key extends string> {
  key: Key;
  label: string;
  receipt?: ReactNode;
  skipped?: boolean;
}

export interface SelectionFlowProps<Key extends string> {
  activeStep: number;
  ariaLabel: string;
  steps: readonly SelectionFlowStep<Key>[];
  onStep: (key: Key) => void;
  navigationDisabled?: boolean;
  hideSelectionSteps?: boolean;
  children: ReactNode;
}

export interface SelectionStepIntroProps {
  eyebrow: string;
  title: ReactNode;
  description: ReactNode;
  descriptionMaxWidth?: number;
}

export const SelectionStepIntro = ({ eyebrow, title, description, descriptionMaxWidth }: SelectionStepIntroProps) => (
  <>
    <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.1em" }}>
      {eyebrow}
    </Typography>
    <Typography
      sx={{
        color: "text.primary",
        fontWeight: 800,
        letterSpacing: "-0.04em",
        fontSize: { xs: "2rem", md: "3.5rem" },
        lineHeight: 1,
        mt: 1,
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: descriptionMaxWidth, mb: { xs: 3, md: 5 } }}>
      {description}
    </Typography>
  </>
);

export const SelectionFlow = <Key extends string>({
  activeStep,
  ariaLabel,
  steps,
  onStep,
  navigationDisabled = false,
  hideSelectionSteps = false,
  children,
}: SelectionFlowProps<Key>) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  useEffect(() => setFiltersExpanded(false), [activeStep, hideSelectionSteps]);
  useStepScroll({ transitionKey: activeStep, targetRef: stageRef, alignment: "end", skipInitial: true });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: activeStep === 3 ? "minmax(0, 1fr)" : { xs: "1fr", lg: "200px minmax(0, 1fr)" },
        gridTemplateRows: activeStep === 3 ? "auto minmax(0, 1fr)" : undefined,
        gap: activeStep === 3 ? 1.5 : { xs: 2, lg: 6 },
        alignItems: activeStep === 3 ? "stretch" : "start",
        flex: activeStep === 3 ? 1 : undefined,
        minHeight: 0,
        overflow: activeStep === 3 ? "hidden" : undefined,
      }}
    >
      {hideSelectionSteps && activeStep === 3 && !filtersExpanded ? (
        <Button
          size="small"
          variant="outlined"
          aria-expanded={false}
          disabled={navigationDisabled}
          onClick={() => setFiltersExpanded(true)}
          sx={{ justifySelf: "start", borderRadius: 0 }}
        >
          Change filters
        </Button>
      ) : (
        <Box
          component="nav"
          aria-label={ariaLabel}
          sx={{
            display: "flex",
            flexDirection: activeStep === 3 ? "row" : { xs: "row", lg: "column" },
            gap: activeStep === 3 ? 0 : 1,
            border: activeStep === 3 ? "1px solid" : 0,
            borderColor: "divider",
          }}
        >
          {steps.map(({ key, label, receipt, skipped = false }, index) => {
            const step = index + 1;
            const complete = step < activeStep;
            return (
              <Box key={key} sx={{ flex: 1, minWidth: 0, position: "relative" }}>
                <ButtonBase
                  disabled={!complete || navigationDisabled || skipped}
                  onClick={() => onStep(key)}
                  sx={{
                    boxSizing: "border-box",
                    display: "block",
                    position: "relative",
                    textAlign: "left",
                    minHeight: { xs: 56, sm: 64 },
                    p: { xs: 1.25, sm: 1.5 },
                    width: "100%",
                    borderLeft: { lg: "2px solid" },
                    borderBottom: { xs: "2px solid", lg: "none" },
                    borderColor: skipped
                      ? "divider"
                      : step === activeStep
                        ? "primary.main"
                        : complete
                          ? "text.secondary"
                          : "divider",
                    bgcolor: skipped
                      ? "transparent"
                      : step === activeStep
                        ? { xs: "primary.main", lg: "rgba(240,236,226,0.06)" }
                        : "transparent",
                    color: skipped
                      ? "text.disabled"
                      : step === activeStep
                        ? { xs: "background.default", lg: "primary.main" }
                        : "text.secondary",
                    transition: "none",
                    "&::after":
                      step === activeStep
                        ? {
                            bgcolor: { xs: "primary.main", lg: "transparent" },
                            bottom: -2,
                            content: '""',
                            height: 2,
                            left: 0,
                            position: "absolute",
                            right: 0,
                          }
                        : undefined,
                    "&.Mui-disabled": {
                      color: skipped
                        ? "text.disabled"
                        : step === activeStep
                          ? { xs: "background.default", lg: "primary.main" }
                          : "text.secondary",
                      opacity: skipped ? 0.45 : 1,
                    },
                    "&:hover": complete
                      ? { bgcolor: "primary.main", borderColor: "primary.main", color: "background.default" }
                      : undefined,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "inherit", fontWeight: 800 }}>
                    0{step}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "inherit", fontWeight: 700, mt: 0.5, textTransform: "uppercase" }}
                  >
                    {label}
                  </Typography>
                  {receipt && (
                    <Typography
                      sx={{
                        color: "inherit",
                        fontSize: "0.58rem",
                        fontWeight: 700,
                        mt: 0.25,
                        opacity: 0.72,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {receipt}
                    </Typography>
                  )}
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        ref={stageRef}
        sx={{
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: activeStep === 3 ? "hidden" : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export interface SelectionClockCardProps {
  active: boolean;
  count: number;
  countText?: ReactNode;
  glyph: ReactNode;
  label: ReactNode;
  onClick?: () => void;
  href?: string;
  ariaLabel?: string;
  loadingProgress?: { loaded: number; total: number } | null;
  disabled?: boolean;
}

export const SelectionClockCard = ({
  active,
  count,
  countText,
  glyph,
  label,
  onClick,
  href,
  ariaLabel,
  loadingProgress = null,
  disabled = false,
}: SelectionClockCardProps) => {
  const [displayedLoaded, setDisplayedLoaded] = useState(0);
  const displayedLoadedRef = useRef(0);
  const lastProgressAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!loadingProgress) {
      displayedLoadedRef.current = 0;
      setDisplayedLoaded(0);
      return;
    }
    const start = displayedLoadedRef.current;
    const target = loadingProgress.loaded;
    const startedAt = performance.now();
    const previousProgressAt = lastProgressAtRef.current ?? startedAt;
    const updateGap = startedAt - previousProgressAt;
    const duration = Math.min(1_000, Math.max(420, updateGap * 8));
    lastProgressAtRef.current = startedAt;
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - startedAt) / duration);
      const next =
        target >= start
          ? Math.min(target, Math.floor(start + (target - start) * elapsed))
          : Math.max(target, Math.ceil(start + (target - start) * elapsed));
      displayedLoadedRef.current = next;
      setDisplayedLoaded(next);
      if (elapsed < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [loadingProgress]);

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      component={href ? "a" : "button"}
      {...(href ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
      aria-label={ariaLabel}
      sx={{
        alignItems: "stretch",
        minWidth: 0,
        textAlign: "left",
        "&:focus-visible > div": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          bgcolor: active ? "primary.main" : "background.paper",
          border: "1px solid",
          borderColor: active ? "primary.main" : "rgba(255,255,255,0.12)",
          color: active ? "#0a0a0a" : "text.primary",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          justifyContent: "space-between",
          minHeight: { xs: 156, sm: 224 },
          p: { xs: 2, sm: 2.5 },
          position: "relative",
          transition: "none",
          width: "100%",
          "&:hover": {
            bgcolor: "primary.main",
            borderColor: "primary.main",
            color: "background.default",
            "& .picker-card-count": { color: "rgba(10,10,10,0.65)" },
          },
          "& > :not(.clock-card-fill)": {
            position: "relative",
            zIndex: 1,
            ...(loadingProgress ? { mixBlendMode: "difference", color: "#f0ece2" } : {}),
          },
        }}
      >
        <Box
          sx={{
            color: "inherit",
            fontSize: { xs: "1.5rem", sm: "2rem" },
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.08em",
            lineHeight: 1,
          }}
        >
          {glyph}
        </Box>
        <Typography
          sx={{
            color: "inherit",
            fontWeight: 800,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            fontSize: { xs: "0.8rem", sm: "1rem" },
          }}
        >
          {label}
        </Typography>
        {loadingProgress && (
          <Box className="clock-card-fill" sx={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
            <Box
              sx={{
                width: `${loadingProgress.total === 0 ? 100 : Math.min(100, (displayedLoaded / loadingProgress.total) * 100)}%`,
                height: "100%",
                bgcolor: "primary.main",
                transition: "width 80ms linear",
              }}
            />
          </Box>
        )}
        <Typography
          className="picker-card-count"
          variant="caption"
          sx={{
            position: "relative",
            color: loadingProgress ? "inherit" : active ? "rgba(10,10,10,0.65)" : "text.secondary",
            fontSize: "0.7rem",
          }}
        >
          {loadingProgress
            ? `${displayedLoaded} / ${loadingProgress.total}`
            : (countText ?? `${count} game${count === 1 ? "" : "s"}`)}
        </Typography>
      </Box>
    </ButtonBase>
  );
};
